import React, { useEffect, useMemo, useState } from 'react'

import {
  Modal,
  TextInput,
  Radio,
  Textarea,
  Button,
  FormElementWrapper,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  bool, func, shape, number, string, oneOfType,
} from 'prop-types'

import {
  MULTIPLE_PARTS_DEFAULTS,
  MULTIPLE_PARTS_UNIT_OPTIONS,
  MULTIPLE_PARTS_UNIT_ABBREVIATIONS,
} from 'Pods/Profile/Products/ModelUploader/Components/constants'

function MultiplePartsModal({
  isOpen,
  onClose: handleClose,
  onSave: handleSave,
  details = MULTIPLE_PARTS_DEFAULTS,
  isSaving = false,
}) {
  const [formValues, setFormValues] = useState(MULTIPLE_PARTS_DEFAULTS)

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const formattedDetails = {
      ...MULTIPLE_PARTS_DEFAULTS,
      ...details,
    }

    const stringifiedDetails = {
      ...formattedDetails,
      numberOfParts:
        formattedDetails.numberOfParts === null
        || typeof formattedDetails.numberOfParts === 'undefined'
          ? ''
          : String(formattedDetails.numberOfParts),
      width:
        formattedDetails.width === null
        || typeof formattedDetails.width === 'undefined'
          ? ''
          : String(formattedDetails.width),
      depth:
        formattedDetails.depth === null
        || typeof formattedDetails.depth === 'undefined'
          ? ''
          : String(formattedDetails.depth),
      height:
        formattedDetails.height === null
        || typeof formattedDetails.height === 'undefined'
          ? ''
          : String(formattedDetails.height),
    }

    setFormValues({
      ...stringifiedDetails,
    })
  }, [isOpen, details])

  const unitOptions = MULTIPLE_PARTS_UNIT_OPTIONS

  const unitAbbreviation = useMemo(
    () => MULTIPLE_PARTS_UNIT_ABBREVIATIONS[formValues.unit] || '',
    [formValues.unit],
  )

  const isUnitSelected = useMemo(() => formValues.unit !== '', [formValues.unit])

  const handleNumberChange = (key) => (event) => {
    const { value } = event.target

    const sanitizedValue = key === 'numberOfParts'
      ? value.replace(/\D/g, '')
      : (value.replace(/[^0-9.]/g, '').match(/^\d*(\.\d*)?/) || [''])[0]

    setFormValues((prevValues) => ({
      ...prevValues,
      [key]: sanitizedValue,
    }))
  }

  const handleAssemblyInstructionsChange = (event) => {
    const { value } = event.target

    setFormValues((prevValues) => ({
      ...prevValues,
      assemblyInstructions: value,
    }))
  }

  const handleUnitChange = (value) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      unit: value,
    }))
  }

  const isSaveDisabled = useMemo(() => {
    if (formValues.numberOfParts !== '') {
      const numberOfParts = Number(formValues.numberOfParts)

      if (
        Number.isNaN(numberOfParts)
        || numberOfParts < 1
        || !Number.isInteger(numberOfParts)
      ) {
        return true
      }
    }

    const hasInvalidDimension = ['width', 'depth', 'height'].some(
      (dimensionKey) => {
        if (formValues[dimensionKey] === '') {
          return false
        }

        const dimensionValue = Number(formValues[dimensionKey])

        return Number.isNaN(dimensionValue) || dimensionValue <= 0
      },
    )

    return hasInvalidDimension
  }, [formValues])

  function onSave() {
    const parsedDimensions = ['width', 'depth', 'height'].reduce(
      (accumulator, dimensionKey) => ({
        ...accumulator,
        [dimensionKey]:
          formValues[dimensionKey] === '' ? null : Number(formValues[dimensionKey]),
      }),
      {},
    )

    handleSave({
      ...formValues,
      numberOfParts:
        formValues.numberOfParts === '' ? null : Number(formValues.numberOfParts),
      ...parsedDimensions,
    })
  }

  return (
    <Modal
      header="Model is split into multiple parts"
      isOpen={isOpen}
      onClose={handleClose}
      width="medium"
      size="medium"
      customClass="multiple-parts-modal"
      canClose={!isSaving}
    >
      <div className="multiple-parts-modal__content">
        <div className="multiple-parts-modal__row">
          <TextInput
            id="multiple-parts-number"
            label="Number of Parts"
            inputType="number"
            min={1}
            isPilot
            size="medium"
            type="primary"
            onChange={handleNumberChange('numberOfParts')}
            value={formValues.numberOfParts}
            wrapperClassName="multiple-parts-modal__field multiple-parts-modal__field--half"
          />
        </div>

        <div className="multiple-parts-modal__row multiple-parts-modal__row--textarea">
          <FormElementWrapper title="Assembly Instructions">
            <Textarea
              isPilot
              id="multiple-parts-assembly-instructions"
              value={formValues.assemblyInstructions}
              onChange={handleAssemblyInstructionsChange}
              placeholder="Provide instructions for assembling the printed parts..."
              maxLength={1000}
              className="multiple-parts-modal__textarea"
            />
          </FormElementWrapper>
        </div>

        <div className="cgt-card cgt-card--border is-rounded">
          <div className="multiple-parts-modal__field multiple-parts-modal__field--units">
            <FormElementWrapper title="Largest part size">
              <div
                className="multiple-parts-modal__unit-options"
                role="radiogroup"
                aria-label="Largest part size"
              >
                {unitOptions.map((option) => (
                  <Radio
                    key={option.value}
                    id={`multiple-parts-unit-${option.value}`}
                    name="multiple-parts-unit"
                    label={option.label}
                    isChecked={formValues.unit === option.value}
                    onChange={() => handleUnitChange(option.value)}
                  />
                ))}
              </div>
            </FormElementWrapper>
          </div>
          <div className="multiple-parts-modal__row multiple-parts-modal__row--dimensions">
            <TextInput
              id="multiple-parts-width"
              label={unitAbbreviation ? `Width (${unitAbbreviation})` : 'Width'}
              inputType="number"
              min={0}
              isPilot
              size="medium"
              type="primary"
              onChange={handleNumberChange('width')}
              value={formValues.width}
              wrapperClassName="multiple-parts-modal__field"
              isDisabled={!isUnitSelected}
            />
            <TextInput
              id="multiple-parts-depth"
              label={unitAbbreviation ? `Depth (${unitAbbreviation})` : 'Depth'}
              inputType="number"
              min={0}
              isPilot
              size="medium"
              type="primary"
              onChange={handleNumberChange('depth')}
              value={formValues.depth}
              wrapperClassName="multiple-parts-modal__field"
              isDisabled={!isUnitSelected}
            />
            <TextInput
              id="multiple-parts-height"
              label={unitAbbreviation ? `Height (${unitAbbreviation})` : 'Height'}
              inputType="number"
              min={0}
              isPilot
              size="medium"
              type="primary"
              onChange={handleNumberChange('height')}
              value={formValues.height}
              wrapperClassName="multiple-parts-modal__field"
              isDisabled={!isUnitSelected}
            />
          </div>
        </div>

        <div className="multiple-parts-modal__actions">
          <Button
            type="ghost"
            size="medium"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="medium"
            onClick={onSave}
            disabled={isSaveDisabled || isSaving}
            loading={isSaving}
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  )
}

MultiplePartsModal.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  onSave: func.isRequired,
  details: shape({
    numberOfParts: oneOfType([number, string]),
    unit: string,
    width: oneOfType([number, string]),
    depth: oneOfType([number, string]),
    height: oneOfType([number, string]),
    assemblyInstructions: string,
  }),
  isSaving: bool,
}

export default MultiplePartsModal
